import evaluacionesService from './EvaluacionesService';
import particularesApiService from './ParticularesApiService';
import { normalizeEvaluacion, normalizePlanillaAlumno, normalizeResumen } from '../utils/evaluaciones';

// Adaptadores para que los componentes de evaluaciones funcionen igual en colegios y en particulares.
// Interfaz: { usaMaterias, listarMaterias, listar, crear, actualizar, eliminar, getPlanilla, guardarNota, getResumen }.

const pick = (source, lower, upper) => source?.[lower] ?? source?.[upper];
const list = (response, lower, upper) => {
  const value = pick(response, lower, upper);
  return Array.isArray(value) ? value : [];
};

const toPayload = ({ nombre, descripcion, fechaHora }) => ({
  nombre,
  descripcion: descripcion || null,
  fechaHora: fechaHora || null
});

const toNotaPayload = ({ nota, ausente, comentario }) => ({
  nota: ausente ? null : nota,
  ausente: Boolean(ausente),
  comentario: comentario || null
});

const parsePlanilla = (response) => ({
  evaluacion: normalizeEvaluacion(pick(response, 'evaluacion', 'Evaluacion')),
  alumnos: list(response, 'alumnos', 'Alumnos').map(normalizePlanillaAlumno)
});

// Colegio: evaluaciones por materia del curso (idCursoMateria obligatorio para listar y crear).
export const crearEvaluacionesApiColegio = ({ idCurso }) => ({
  usaMaterias: true,
  async listarMaterias() {
    const response = await evaluacionesService.getMateriasEvaluables(idCurso);
    return list(response, 'materias', 'Materias').map((m) => ({
      idCursoMateria: String(pick(m, 'idCursoMateria', 'IdCursoMateria') || ''),
      nombre: pick(m, 'nombre', 'Nombre') || 'Materia'
    }));
  },
  async listar(idCursoMateria) {
    const response = await evaluacionesService.getEvaluaciones(idCursoMateria);
    return list(response, 'evaluaciones', 'Evaluaciones').map(normalizeEvaluacion);
  },
  async crear(data, idCursoMateria) {
    const response = await evaluacionesService.createEvaluacion(idCursoMateria, toPayload(data));
    return normalizeEvaluacion(pick(response, 'evaluacion', 'Evaluacion'));
  },
  async actualizar(id, data) {
    const response = await evaluacionesService.updateEvaluacion(id, toPayload(data));
    return normalizeEvaluacion(pick(response, 'evaluacion', 'Evaluacion'));
  },
  eliminar: (id) => evaluacionesService.deleteEvaluacion(id),
  async getPlanilla(id) {
    return parsePlanilla(await evaluacionesService.getPlanilla(id));
  },
  async guardarNota(idEvaluacion, idAlumno, data) {
    const response = await evaluacionesService.guardarNota(idEvaluacion, idAlumno, toNotaPayload(data));
    return normalizePlanillaAlumno(pick(response, 'nota', 'Nota'));
  },
  async getResumen(idCursoMateria = null) {
    const response = await evaluacionesService.getResumen(idCurso, idCursoMateria);
    return normalizeResumen(pick(response, 'resumen', 'Resumen'));
  }
});

// Particular: evaluaciones por curso (no hay materias).
export const crearEvaluacionesApiParticular = ({ idCurso }) => ({
  usaMaterias: false,
  listarMaterias: async () => [],
  async listar() {
    const response = await particularesApiService.getCursoEvaluaciones(idCurso);
    return list(response, 'evaluaciones', 'Evaluaciones').map(normalizeEvaluacion);
  },
  async crear(data) {
    const response = await particularesApiService.createCursoEvaluacion(idCurso, toPayload(data));
    return normalizeEvaluacion(pick(response, 'evaluacion', 'Evaluacion'));
  },
  async actualizar(id, data) {
    const response = await particularesApiService.updateEvaluacion(id, toPayload(data));
    return normalizeEvaluacion(pick(response, 'evaluacion', 'Evaluacion'));
  },
  eliminar: (id) => particularesApiService.deleteEvaluacion(id),
  async getPlanilla(id) {
    return parsePlanilla(await particularesApiService.getEvaluacionPlanilla(id));
  },
  async guardarNota(idEvaluacion, idAlumno, data) {
    const response = await particularesApiService.guardarEvaluacionNota(idEvaluacion, idAlumno, toNotaPayload(data));
    return normalizePlanillaAlumno(pick(response, 'nota', 'Nota'));
  },
  async getResumen() {
    const response = await particularesApiService.getCursoResumenAcademico(idCurso);
    return normalizeResumen(pick(response, 'resumen', 'Resumen'));
  }
});
